/**
 * Coded reports.
 *
 * A report is TypeScript in this repo: a definition here (the values it
 * collects, how it evaluates them) and a React component under
 * `src/frontend/reports/` (how it renders). Both frontend and backend import
 * the definition, so the verdict stamped on a published version is computed
 * by the same code that drew it on screen.
 */

import type { RunSnapshot, RunTrace, RunTraceRequest } from './runs';
import type { Requirement } from './bindings';
import type { BlockDefinition } from './blocks';
import type { InputSheet, InputValues } from './inputs';

/**
 * The contract version a report bundle is compiled against. Bumped when
 * ReportDefinition, RunReportContext or the SDK change so that an existing bundle
 * would misbehave; a bundle built against another version is refused rather
 * than run. Built-in reports always match: they compile with the app.
 */
export const REPORT_CONTRACT_VERSION = '1';

/** A cell of a fillable table. Numbers stay numbers; an empty cell is null. */
export type CellValue = string | number | null;
export type TableRow = Record<string, CellValue>;

export type TableColumnType = 'string' | 'number' | 'integer' | 'choice';

/** What a fillable table's column accepts. The UI builds its editor from this; CSV import coerces to it. */
export interface TableColumnSpec {
  id: string;
  title: string;
  type: TableColumnType;
  choices?: string[];
  unit?: string;
  help?: string;
  /** a CSS width for the column, e.g. "8rem" */
  width?: string;
}

export type ReportSeverity = 'fail' | 'warn' | 'info';

/**
 * What a finding found, for `verdictFrom` (verdict.ts): a `defect` fails the
 * report, `invalid` makes the record unreliable, a `gap` leaves it incomplete,
 * a `note` changes nothing, a `pass` records a check that held.
 */
export type FindingKind = 'defect' | 'invalid' | 'gap' | 'note' | 'pass';

/** One check the report makes over its inputs. */
export interface ReportFinding {
  id: string;
  severity: ReportSeverity;
  passed: boolean;
  message: string;
  /** absent on findings made before kinds existed; read from severity and passed (verdict.ts `kindOf`) */
  kind?: FindingKind;
}

/**
 * - `passed`: every check passed and the report's own completeness rule is met
 * - `failed`: a `fail`-severity check failed
 * - `incomplete`: nothing failed, but the report cannot yet be closed
 */
export type ReportVerdict = 'passed' | 'failed' | 'incomplete';

export interface ReportEvaluation {
  verdict: ReportVerdict;
  /** the word the report prints for its verdict, e.g. VERIFIED / NOT VERIFIED / FAIL */
  verdictLabel: string;
  findings: ReportFinding[];
}

/**
 * What a report reads.
 *
 * - `run`: a test report on one uploaded procedure run, which it reads itself
 *   (`RunReportContext`).
 * - `inputs`: a report filled in from its bundle's input sheet (`inputs.json`,
 *   inputs.ts) — by hand today, perhaps from a run one day; it reads only what
 *   was entered (`InputsReportContext`).
 */
export type ReportSubject = 'run' | 'inputs';
export const REPORT_SUBJECTS: readonly ReportSubject[] = ['run', 'inputs'];

/**
 * The report template a run report was created from: its cover fields, as
 * a legacy report's cover page printed them. What the template says about the
 * test (requirement, method, plan) is the template author's; a report prints
 * it where it has nothing better.
 */
export interface RunReportTemplateInfo {
  id: string;
  title: string;
  reportCreator: string;
  requirement: string;
  testMethod: string;
  testPlanNumber: string;
  testPlanRevision: string;
}

/** A sign-off line a report prints, e.g. the test engineer and a reviewer; the name is typed into the report's values. */
export interface SignoffSpec {
  id: string;
  label: string;
  /** the line may stay blank; the outline doesn't list it as left to do */
  optional?: boolean;
}

/** What the app hands a run report besides its own values. Frozen into every published version. */
export interface RunReportContext {
  /** the run the report is on, flattened: steps, readings, attached files, equipment, DUT */
  run: RunSnapshot;
  project: {
    id: string;
    title: string;
  } | null;
  /** the point files the definition asked for with `traces`, decimated, under the key each was asked for */
  traces: Record<string, RunTrace>;
  /** the report template the report was created from; null when it has since been deleted */
  reportTemplate?: RunReportTemplateInfo | null;
  /**
   * The definition's settings, normalized: its layout, section map, bindings
   * and whatever else it declares. Written in the report's code, never by a
   * template's owner, so every run of every template on the report shares them.
   */
  settings?: ReportValues;
}

/** What the app hands a report filled in from its input sheet, besides its own values. Frozen into every published version. */
export interface InputsReportContext {
  /** the bundle's input sheet the inputs were entered against */
  sheet: InputSheet;
  /** what was entered, normalized to the sheet: by input id (inputs.ts) */
  inputs: InputValues;
  /** each waveform entered, decimated, under its `traceKey` */
  traces: Record<string, RunTrace>;
  /** the report being filled in; null in a fixture or the studio's preview */
  report: { id: string; title: string } | null;
  /** the definition's settings, normalized, as for a run report */
  settings?: ReportValues;
}

/** A report's values as stored: any JSON object. A report declares its own shape and narrows to it. */
export type ReportValues = Record<string, unknown>;

export interface ReportDefinition<V extends object = ReportValues, M extends ReportEvaluation = ReportEvaluation, C extends object = RunReportContext> {
  /** stable id stored on report templates and run reports; never rename once reports exist */
  key: string;
  /** what the report reads; absent means `run` */
  subject?: ReportSubject;
  title: string;
  description: string;
  /** bumped when the evaluation or the document changes in a way a reader should know about */
  version: string;
  defaultValues: () => V;
  /** Pure: the whole derived document model from values + context. */
  evaluate: (values: V, context: C) => M;
  /** Bring stored values (possibly from an older shape) up to the current shape. */
  normalizeValues: (raw: unknown) => V;
  /**
   * The point files (waveform captures) to read from the
   * run, named by the file ids its readings carry. The app reads each one,
   * decimates it and hands it back in `context.traces` under the request's
   * key. Pure. Only files attached to the run are ever read.
   */
  traces?: (run: RunSnapshot) => RunTraceRequest[];
  /**
   * The verdict of record: verdict, label and findings, and nothing about
   * layout. Publishing calls this when it is present, `evaluate` otherwise.
   * `defineRunReport` sets `evaluate` to it, so a report written that way
   * returns only its judgement and lets the document derive the rest. Pure.
   */
  judge?: (values: V, context: C) => ReportEvaluation;
  /**
   * Settings: what every report the definition makes shares — a composed
   * report's layout, its section map, its bindings. The author writes them as
   * defaults in code; the app hands them back normalized in `context.settings`.
   */
  defaultSettings?: () => ReportValues;
  normalizeSettings?: (raw: unknown) => ReportValues;
  /** what the report reads from the run, by identifier, checked against the procedure on the template page (bindings.ts) */
  requires?: Record<string, Requirement>;
  /** the sign-off lines the report prints; names are typed into `values.signoffs` by role */
  signoffs?: SignoffSpec[];
  /** the report's own block types, beside the SDK's, for a composed layout (blocks.ts) */
  blocks?: BlockDefinition[];
}

/** The subject a definition declares, with the default filled in; null for one this app has no reports for. */
export function reportSubject(definition: { subject?: unknown }): ReportSubject | null {
  if (definition.subject === undefined || definition.subject === null) return 'run';
  return (REPORT_SUBJECTS as readonly unknown[]).includes(definition.subject) ? (definition.subject as ReportSubject) : null;
}

/**
 * The definition as the run report it is, or null for a subject this app has
 * no reports for (a bundle may declare anything).
 */
export function asRunReport<V extends object, M extends ReportEvaluation>(definition: ReportDefinition<V, M>): ReportDefinition<V, M, RunReportContext> | null {
  return reportSubject(definition) === 'run' ? definition : null;
}

/** The definition as a report filled in from its input sheet, or null for any other subject. */
export function asInputsReport<V extends object, M extends ReportEvaluation>(definition: ReportDefinition<V, M, never>): ReportDefinition<V, M, InputsReportContext> | null {
  return reportSubject(definition) === 'inputs' ? (definition as unknown as ReportDefinition<V, M, InputsReportContext>) : null;
}
